import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "../../shared/Button/Button";
import { tCommon } from "../../i18n/useAppTranslation";
import classes from "./AppUpdatePrompt.module.scss";
import {
  APP_UPDATE_CHECK_INTERVAL,
  getCurrentMainAsset,
  getLatestMainAsset,
  isNewBuildAvailable,
} from "./utilities";

export function AppUpdatePrompt() {
  const [availableAsset, setAvailableAsset] = useState(null);
  const dismissedAssetRef = useRef(null);
  const isCheckingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      const currentAsset = getCurrentMainAsset();
      const latestAsset = await getLatestMainAsset();

      if (
        isNewBuildAvailable(currentAsset, latestAsset) &&
        dismissedAssetRef.current !== latestAsset
      ) {
        setAvailableAsset(latestAsset);
      }
    } catch (e) {
      // Update checks must never interrupt normal application use.
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return undefined;
    }

    checkForUpdate();
    const intervalId = window.setInterval(
      checkForUpdate,
      APP_UPDATE_CHECK_INTERVAL,
    );
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdate();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    if (!availableAsset) {
      return undefined;
    }

    const dismiss = () => {
      dismissedAssetRef.current = availableAsset;
      setAvailableAsset(null);
    };
    const toastId = toast(
      <div className={classes.root}>
        <strong className={classes.title}>{tCommon("appUpdate.title")}</strong>
        <p className={classes.description}>
          {tCommon("appUpdate.description")}
        </p>
        <div className={classes.actions}>
          <Button size="tiny" onClick={() => window.location.reload()}>
            {tCommon("appUpdate.reload")}
          </Button>
          <Button
            className={classes.laterButton}
            color="black"
            size="tiny"
            variant="text"
            onClick={dismiss}
          >
            {tCommon("appUpdate.later")}
          </Button>
        </div>
      </div>,
      {
        toastId: "app-update",
        autoClose: false,
        bodyClassName: classes.toastBody,
        className: classes.toast,
        closeButton: false,
        closeOnClick: false,
        draggable: true,
        hideProgressBar: true,
      },
    );

    return () => toast.dismiss(toastId);
  }, [availableAsset]);

  return null;
}
